import React, { useState } from 'react'
import { Form, Input, Button, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import { SendHorizontal } from 'lucide-react'

interface CommentFormProps {
  initialValue?: string
  onSubmit: (text: string) => void | Promise<void>
  onCancel?: () => void
  submitText?: string
}

/**
 * Form for creating/editing comments
 */
export const CommentForm: React.FC<CommentFormProps> = ({ initialValue = '', onSubmit, onCancel, submitText }) => {
  const { t } = useTranslation()
  const [text, setText] = useState(initialValue)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    if (!text.trim()) return

    setIsSubmitting(true)
    try {
      await onSubmit(text.trim())
      setText('') // Clear after submit
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form onFinish={handleSubmit}>
      <Form.Item>
        <Input.TextArea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={t('global:comment') || 'Comment'}
          rows={8}
          maxLength={1000}
          showCount
          styles={{
            count: {
              left: 0
            }
          }}
        />
      </Form.Item>
      <Form.Item style={{ marginBottom: 0, display: 'flex', justifyContent: 'flex-end' }}>
        <Space>
          {onCancel && (
            <Button onClick={onCancel} disabled={isSubmitting}>
              {t('global:cancel', 'Cancel')}
            </Button>
          )}
          <Button
            icon={<SendHorizontal size={14} strokeWidth={2} />}
            iconPosition="end"
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            disabled={!text.trim()}
          >
            {submitText || t('global:save', 'Save')}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  )
}
